import { HttpTransportType, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import * as Constants from "common/helpers/constants";
import reduxSignalRMiddleware, { withCallbacks } from "common/realtime/redux-signalr-middleware";
import Guid from "common/values/guid/guid";
import Session from "users/session/session";
import ProposalAPIService, { ProposalForbiddenError } from "work/entities/proposal/api/proposal-api-service";
import { addProposal, removeProposal, replaceProposalBuilder, updateAllQueriesLoading, updateProposal } from "work/entities/proposal/store/proposals-redux-slice";
import Date from "common/values/date/date";
import Proposal from "work/entities/proposal/proposal";
import moment from "moment";
import WorkAgreementAPIService from "work/entities/work-agreement/api/work-agreement-api-service";
import ProposalBuilder from "work/entities/proposal/utils/proposal-builder";

export const proposalHubConnection = new HubConnectionBuilder()
  .configureLogging(LogLevel.Debug)
  .withUrl(`${Constants.apiBaseUrl}/proposalHub`, {
    skipNegotiation: true,
    transport: HttpTransportType.WebSockets,
    accessTokenFactory: () => Session.loadFromStorage(() => {}).authToken?.value ?? "",
  })
  .withAutomaticReconnect()
  .build();

interface ProposalCreatedEvent { id: string }
interface ProposalUpdatedEvent extends ProposalCreatedEvent { lastUpdated: string; replacedBy?: string }

const callbacks = withCallbacks()
  .add("proposal-created", (payload: unknown) => (dispatch, getState) => {
    const { id } = (payload as ProposalCreatedEvent) ?? {};
    if (!id) return;

    const state = getState() as {
      proposals: { byId: { entries: Record<string, Proposal | undefined> } };
    };
    if (state.proposals.byId.entries[id]) return; // already present

    dispatch(updateAllQueriesLoading(true));

    const session = Session.loadFromStorage(() => {});
    const workAgreementApiService = new WorkAgreementAPIService(session);
    const apiService = new ProposalAPIService(session, workAgreementApiService);
    const proposalId = new Guid(id);

    apiService
      .getProposalById(proposalId)
      .then(({ proposal, userProposalBuilder }) => {
        if (proposal.id && (getState() as typeof state).proposals.byId.entries[proposal.id.value]) return;
        dispatch(addProposal(proposal));
        if (userProposalBuilder) {
          dispatch(replaceProposalBuilder({ proposalId, builder: userProposalBuilder }));
        }
      })
      .finally(() => dispatch(updateAllQueriesLoading(false)));
  })
  .add("proposal-updated", (payload: unknown) => (dispatch, getState) => {
    const { id, lastUpdated, replacedBy } = (payload as ProposalUpdatedEvent) ?? {};
    if (!id) return;

    const state = getState() as {
      proposals: { byId: { entries: Record<string, Proposal | undefined> }; builders: { entries: Record<string, unknown> } };
    };

    const incomingLastUpdated = lastUpdated ? new Date(moment(lastUpdated)) : undefined;
    const existing = state.proposals.byId.entries[id];
    if (existing?.lastUpdated && incomingLastUpdated && !incomingLastUpdated.isAfter(existing.lastUpdated)) {
      return; // ignore stale update
    }

    if (existing && replacedBy) {
      existing.replacedBy = new Guid(replacedBy);
      dispatch(addProposal(existing));
      return;
    }

    const session = Session.loadFromStorage(() => {});
    const workAgreementApiService = new WorkAgreementAPIService(session);
    const apiService = new ProposalAPIService(session, workAgreementApiService);
    const proposalId = new Guid(id);

    apiService
      .getProposalById(proposalId)
      .then(({ proposal, userProposalBuilder, userRedlining }) => {
        if (!proposal.id) {
          console.error("Proposal does not have an id", proposal);
          return;
        }

        const existingBuilder = state.proposals.builders.entries[proposal.id.value] as ProposalBuilder;

        if (userProposalBuilder && existingBuilder) {
          const builderToMerge: ProposalBuilder = userProposalBuilder.lastUpdated.isAfter(existingBuilder.lastUpdated)
            ? userProposalBuilder
            : existingBuilder;
          dispatch(replaceProposalBuilder({ proposalId, builder: builderToMerge.mergeUpdatedSpec(proposal.spec) }));
        } else if (existingBuilder) {
          dispatch(replaceProposalBuilder({ proposalId, builder: existingBuilder.mergeUpdatedSpec(proposal.spec) }));
        } else if (userProposalBuilder) {
          dispatch(replaceProposalBuilder({ proposalId, builder: userProposalBuilder.mergeUpdatedSpec(proposal.spec) }));
        }

        let merged: Proposal = proposal;
        const existingProposal = existing;

        if (!userRedlining && existingProposal?.redline && !proposal.details?.redlining) {
          merged = merged.updateRedline(existingProposal.redline);
        } else if (userRedlining && existingProposal?.redline?.lastUpdated?.isAfter(userRedlining.lastUpdated) && !proposal.details?.redlining) {
          merged = merged.updateRedline(existingProposal.redline);
        } else if (userRedlining && !existingProposal?.redline?.lastUpdated?.isAfter(userRedlining.lastUpdated) && !proposal.details?.redlining) {
          merged = merged.updateRedline(userRedlining);
        } else if (
          (!userRedlining && existingProposal?.redline && proposal.details?.redlining) ||
          (userRedlining && existingProposal?.redline?.lastUpdated?.isAfter(userRedlining.lastUpdated) && proposal.details?.redlining)
        ) {
          merged = merged.mergeSharedRedline(
            existingProposal.redline,
            existingProposal.redline.lastUpdated.isAfter(
              merged.details?.redliningUpdated || new Date(moment().subtract(1000, "years"))
            )
          );
        } else if (userRedlining && !existingProposal?.redline?.lastUpdated?.isAfter(userRedlining.lastUpdated) && proposal.details?.redlining) {
          merged = merged.mergeSharedRedline(
            userRedlining,
            userRedlining.lastUpdated.isAfter(
              merged.details?.redliningUpdated || new Date(moment().subtract(1000, "years"))
            )
          );
        }

        merged.updateLastSavedRedline();
        if (existingProposal) {
          dispatch(updateProposal(merged));
        } else {
          dispatch(addProposal(merged));
        }
      })
      .catch((error) => {
        if (error instanceof ProposalForbiddenError) {
          dispatch(removeProposal(new Guid(id)));
        }
      });
  })
  .add("proposal-deleted", (payload: unknown) => (dispatch) => {
    const { id } = (payload as ProposalCreatedEvent) ?? {};
    if (!id) return;
    dispatch(removeProposal(new Guid(id)));
  });

export const proposalsSignalRReduxMiddleware = reduxSignalRMiddleware({
  callbacks,
  connection: proposalHubConnection,
  shouldConnectionStartImmediately: true,
});
